import { apiFetch } from './client';

// Webshop + lagkassa admin client (mobile-app §8). Mirrors the JSON the Go
// handler in internal/api/shop.go emits. Reads come back as { kind: [...] }
// envelopes; the helpers below unwrap so callers get the array directly.

export interface StoreSummary {
  id: string;
  team_id: string;
  team_name: string;
  name: string;
  slug: string;
  status: string;
  balance_minor: number;
  currency: string;
  product_count: number;
  open_orders: number;
  created_at: string;
}

export interface TeamFund {
  team_id: string;
  team_name: string;
  balance_minor: number;
  currency: string;
  entries: number;
}

export interface FundEntry {
  id: string;
  kind:
    | 'sale'
    | 'fee_collected'
    | 'refund'
    | 'payout'
    | 'expense'
    | 'adjustment'
    | 'provider_fee';
  amount_minor: number;
  currency: string;
  order_id?: string;
  payout_id?: string;
  note: string;
  created_at: string;
  created_by_user_id?: string;
}

export interface OrderSummary {
  id: string;
  status: 'pending' | 'paid' | 'fulfilled' | 'cancelled' | 'refunded';
  total_minor: number;
  currency: string;
  payment_method: string;
  buyer_name: string;
  created_at: string;
}

export interface CampaignSummary {
  id: string;
  name: string;
  status: 'active' | 'ended';
  team_fund_share_bps: number;
  seller_count: number;
  starts_at?: string;
  ends_at?: string;
}

export interface PayoutSummary {
  id: string;
  team_id: string;
  team_name: string;
  amount_minor: number;
  currency: string;
  status: 'requested' | 'approved' | 'paid' | 'rejected';
  note: string;
  requested_by_user_id?: string;
  requested_at: string;
  approved_by_user_id?: string;
  approved_at?: string;
}

export interface ProductSummary {
  id: string;
  title: string;
  description: string;
  status: string;
  image_object_key: string;
  price_minor: number;
  currency: string;
}

export interface ProductInput {
  title: string;
  description: string;
  currency: string;
  price_minor: number;
  image_object_key: string;
}

function orgPath(slug: string, suffix: string): string {
  return `/api/v1/organizations/${encodeURIComponent(slug)}/shop${suffix}`;
}

export async function listStores(token: string | null, slug: string): Promise<StoreSummary[]> {
  const res = await apiFetch<{ stores: StoreSummary[] }>(orgPath(slug, '/stores'), { token });
  return res.stores ?? [];
}

export async function getStore(
  token: string | null,
  slug: string,
  storeID: string,
): Promise<StoreSummary> {
  return apiFetch<StoreSummary>(orgPath(slug, `/stores/${encodeURIComponent(storeID)}`), {
    token,
  });
}

export async function createStore(
  token: string | null,
  slug: string,
  body: { team_id: string; name: string; slug?: string },
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(orgPath(slug, '/stores'), {
    method: 'POST',
    body,
    token,
  });
}

export async function listTeamFunds(
  token: string | null,
  slug: string,
): Promise<TeamFund[]> {
  const res = await apiFetch<{ team_funds: TeamFund[] }>(orgPath(slug, '/team-funds'), { token });
  return res.team_funds ?? [];
}

export async function getFundBalance(
  token: string | null,
  slug: string,
  teamID: string,
): Promise<{ team_id: string; balance_minor: number }> {
  return apiFetch(orgPath(slug, `/teams/${encodeURIComponent(teamID)}/fund-balance`), { token });
}

export async function listFundEntries(
  token: string | null,
  slug: string,
  teamID: string,
  limit = 100,
): Promise<FundEntry[]> {
  const res = await apiFetch<{ entries: FundEntry[] }>(
    orgPath(slug, `/teams/${encodeURIComponent(teamID)}/fund-entries?limit=${limit}`),
    { token },
  );
  return res.entries ?? [];
}

export async function postFundEntry(
  token: string | null,
  slug: string,
  teamID: string,
  body: { kind: FundEntry['kind']; amount_minor: number; currency: string; note?: string },
): Promise<{ id: string }> {
  return apiFetch(orgPath(slug, `/teams/${encodeURIComponent(teamID)}/fund-entries`), {
    method: 'POST',
    body,
    token,
  });
}

export async function listOrders(
  token: string | null,
  slug: string,
  storeID: string,
  limit = 100,
): Promise<OrderSummary[]> {
  const res = await apiFetch<{ orders: OrderSummary[] }>(
    orgPath(slug, `/stores/${encodeURIComponent(storeID)}/orders?limit=${limit}`),
    { token },
  );
  return res.orders ?? [];
}

export async function listProducts(
  token: string | null,
  slug: string,
  storeID: string,
): Promise<ProductSummary[]> {
  const res = await apiFetch<{ products: ProductSummary[] }>(
    orgPath(slug, `/stores/${encodeURIComponent(storeID)}/products`),
    { token },
  );
  return res.products ?? [];
}

export async function createProduct(
  token: string | null,
  slug: string,
  storeID: string,
  body: ProductInput,
): Promise<{ id: string }> {
  return apiFetch(orgPath(slug, `/stores/${encodeURIComponent(storeID)}/products`), {
    method: 'POST',
    body,
    token,
  });
}

export async function updateProduct(
  token: string | null,
  slug: string,
  productID: string,
  body: ProductInput,
): Promise<void> {
  await apiFetch<void>(orgPath(slug, `/products/${encodeURIComponent(productID)}`), {
    method: 'PATCH',
    body,
    token,
  });
}

export async function listCampaigns(
  token: string | null,
  slug: string,
  storeID: string,
): Promise<CampaignSummary[]> {
  const res = await apiFetch<{ campaigns: CampaignSummary[] }>(
    orgPath(slug, `/stores/${encodeURIComponent(storeID)}/campaigns`),
    { token },
  );
  return res.campaigns ?? [];
}

export async function startCampaign(
  token: string | null,
  slug: string,
  storeID: string,
  body: {
    name: string;
    team_fund_share_bps: number;
    starts_at?: string;
    ends_at?: string;
  },
): Promise<{ id: string }> {
  return apiFetch(orgPath(slug, `/stores/${encodeURIComponent(storeID)}/campaigns`), {
    method: 'POST',
    body,
    token,
  });
}

export async function listPayouts(
  token: string | null,
  slug: string,
  statusFilter?: string,
): Promise<PayoutSummary[]> {
  const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
  const res = await apiFetch<{ payouts: PayoutSummary[] }>(orgPath(slug, `/payouts${qs}`), {
    token,
  });
  return res.payouts ?? [];
}

export async function requestPayout(
  token: string | null,
  slug: string,
  teamID: string,
  body: { amount_minor: number; currency: string; note?: string },
): Promise<{ id: string }> {
  return apiFetch(orgPath(slug, `/teams/${encodeURIComponent(teamID)}/payouts`), {
    method: 'POST',
    body,
    token,
  });
}

export async function approvePayout(
  token: string | null,
  slug: string,
  payoutID: string,
): Promise<void> {
  await apiFetch<void>(orgPath(slug, `/payouts/${encodeURIComponent(payoutID)}/approve`), {
    method: 'POST',
    token,
  });
}

// formatMoney is the canonical minor-units → display string used by every shop
// page. Defaults to grouping by Swedish convention (non-breaking space) since
// SportAdmin is Nordic-focused; pass a locale to override. Empty currency (the
// no-entries-yet state) renders as a bare number.
export function formatMoney(amountMinor: number, currency: string, locale = 'sv-SE'): string {
  const amount = amountMinor / 100;
  if (!currency) {
    return amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString(locale, { minimumFractionDigits: 2 })} ${currency}`;
  }
}
